import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import ConfirmDialog from '../../../components/ConfirmDialog';
import useErrorToast from '../../../hooks/useErrorToast';
import { translate } from '../../../i18n';
import { CreateUserMembershipDto, UserMembershipDto } from '../../../models/user-membership/dto/user-membership.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCreateUserMembership } from '../hooks/useCreateUserMembership';
import { useDeleteUserMembership } from '../hooks/useDeleteMembership';
import { useUpdateUserMembership } from '../hooks/useUpdateUserMembership';
import UserMembershipTypePicker from './UserMembershipTypePicker';

type Props = {
    userId: string;
    selectedMembership?: UserMembershipDto;
    setSelectedMembership?: (membership?: UserMembershipDto) => void;
};

const initialFormData: CreateUserMembershipDto = {
    startDate: '',
    endDate: '',
    isActive: true,
    membershipTypeId: '',
};

export default function AddUserMembershipModal({ userId, selectedMembership, setSelectedMembership }: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const showError = useErrorToast();
    const { selectedOrganization } = useSelectedOrganization();
    const [formData, setFormData] = useState<CreateUserMembershipDto>({ ...initialFormData });
    const { mutateAsync: create, isLoading: isCreating } = useCreateUserMembership(userId);
    const { mutateAsync: update, isLoading: isUpdating } = useUpdateUserMembership(selectedMembership?.id);
    const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDeleteUserMembership(selectedMembership?.id);

    const handleInputChange = (key: keyof typeof formData, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    useEffect(() => {
        if (selectedMembership) {
            setFormData({
                ...formData,
                startDate: new Date(selectedMembership.startDate).toISOString().split('T')[0],
                endDate: new Date(selectedMembership.endDate).toISOString().split('T')[0],
                isActive: selectedMembership.isActive,
                membershipTypeId: selectedMembership?.type?.id,
            });
            onOpen();
        }
    }, [selectedMembership]);

    const onModalClose = () => {
        setSelectedMembership(undefined);
        setFormData({ ...initialFormData });
        onClose();
    };

    const handleSubmit = async () => {
        try {
            if (selectedMembership) {
                await update(formData);
            } else {
                await create(formData);
            }
            onModalClose();
        } catch (err) {
            showError(err);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteAsync(selectedMembership);
            onDeleteClose();
            onModalClose();
        } catch (err) {
            showError(err);
        }
    };

    const isStartDateValid = Boolean(formData.startDate);
    // MYSPORTSET-API-R3 — this used to read `!formData.endDate || ...`, i.e.
    // an EMPTY end date counted as valid. The initial form state seeds
    // `endDate: ''`, so Submit was enabled with the box untouched and the
    // request went out as `endDate: ''`. On the server that column is
    // `not null` and `new Date('')` is `Invalid Date`, which does not throw
    // until MikroORM flushes it — the admin got an opaque 500 ("Invalid time
    // value") naming neither the field nor the endpoint. The API now answers
    // 400, and this stops the request being sent at all.
    const isEndDateValid = Boolean(formData.endDate) && new Date(formData.endDate) >= new Date(formData.startDate);
    const isMembershipTypeValid = Boolean(formData.membershipTypeId);
    const isFormValid = isStartDateValid && isEndDateValid && isMembershipTypeValid;

    const isEditing = Boolean(selectedMembership);

    return (
        <>
            <Button size="sm" colorScheme="brand" onClick={onOpen}>
                {translate('userMembership.assign_membership')}
            </Button>

            <FormModal
                isOpen={isOpen}
                onClose={onModalClose}
                title={translate(isEditing ? 'userMembership.edit_membership_type' : 'userMembership.add_new_membership')}
                onSubmit={handleSubmit}
                submitLabel={translate('common.submit')}
                cancelLabel={translate('common.cancel')}
                isSubmitDisabled={!isFormValid}
                isSubmitting={isCreating || isUpdating}
                destructiveAction={
                    isEditing
                        ? {
                              label: translate('common.delete'),
                              onClick: onDeleteOpen,
                          }
                        : undefined
                }
            >
                <FormStack>
                    {/* Membership type — uses custom picker so stays as FormControl */}
                    <FormControl isInvalid={!isMembershipTypeValid}>
                        <FormLabel>{translate('userMembership.membershipType')}</FormLabel>
                        <UserMembershipTypePicker
                            organizationId={selectedOrganization?.id}
                            value={formData.membershipTypeId}
                            onChange={(value) => handleInputChange('membershipTypeId', value)}
                        />
                        <FormErrorMessage>{translate('userMembership.invalid_membership_type')}</FormErrorMessage>
                    </FormControl>

                    <FormField
                        label={translate('userMembership.startDate')}
                        type="date"
                        value={formData.startDate}
                        onChange={(v) => handleInputChange('startDate', v)}
                        error={!isStartDateValid ? translate('userMembership.invalid_start_date') : undefined}
                    />

                    <FormField
                        label={translate('userMembership.endDate')}
                        type="date"
                        value={formData.endDate}
                        min={formData.startDate}
                        onChange={(v) => handleInputChange('endDate', v)}
                        error={!isEndDateValid ? translate('userMembership.invalid_end_date') : undefined}
                    />

                    <FormField
                        label={translate('userMembership.is_active')}
                        type="switch"
                        isChecked={formData.isActive}
                        onChange={(checked) => handleInputChange('isActive', checked)}
                    />
                </FormStack>
            </FormModal>

            {/* Delete confirmation */}
            <ConfirmDialog
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                onConfirm={handleDelete}
                title={translate('common.delete')}
                body={translate('userMembership.delete_membership_confirmation')}
                confirmLabel={translate('common.delete')}
                cancelLabel={translate('common.cancel')}
                isConfirming={isDeleting}
            />
        </>
    );
}
